// src/Pages/BlogPromo/BlogPromo.jsx

import React, { useState, useEffect } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../Firebase/firebaseConfig";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "./BlogPromo.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Spinner from 'react-bootstrap/Spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from 'react-bootstrap/Pagination'; // Import Bootstrap Pagination

const BlogPromo = () => {
  const { t, i18n } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const isRTL = i18n.language === "ar";
  const [isLoading, setIsLoading] = useState(true);

  // Separate Pagination States
  const [currentPageUpcoming, setCurrentPageUpcoming] = useState(1);
  const [currentPageLastNews, setCurrentPageLastNews] = useState(1);
  const blogsPerPage = 2; // Number of blogs per page per category

  useEffect(() => {
    const q = query(collection(db, "blogs"), orderBy("date", "desc")); // Order by date descending
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedBlogs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBlogs(fetchedBlogs);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching blogs:", error);
      toast.error(t("errorFetchingBlogs"));
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [t]);

  // Title in the current language, falling back to English, then any available translation
  const titleOf = (blog) =>
    blog.title?.[i18n.language] || blog.title?.en || Object.values(blog.title || {}).find(Boolean) || t("noTitle");

  // Separate blogs by category
  const upcomingEvents = blogs.filter(blog => blog.category === "upcoming-events");
  const lastNews = blogs.filter(blog => blog.category === "last-news");

  // Calculate pagination details for Upcoming News
  const totalPagesUpcoming = Math.ceil(upcomingEvents.length / blogsPerPage);
  const indexOfLastBlogUpcoming = currentPageUpcoming * blogsPerPage;
  const indexOfFirstBlogUpcoming = indexOfLastBlogUpcoming - blogsPerPage;
  const currentUpcoming = upcomingEvents.slice(indexOfFirstBlogUpcoming, indexOfLastBlogUpcoming);

  // Calculate pagination details for Last News
  const totalPagesLastNews = Math.ceil(lastNews.length / blogsPerPage);
  const indexOfLastBlogLastNews = currentPageLastNews * blogsPerPage;
  const indexOfFirstBlogLastNews = indexOfLastBlogLastNews - blogsPerPage;
  const currentLastNews = lastNews.slice(indexOfFirstBlogLastNews, indexOfLastBlogLastNews);

  const handlePageChangeUpcoming = (pageNumber) => {
    setCurrentPageUpcoming(pageNumber);
    // Removed scroll to top
  };

  const handlePageChangeLastNews = (pageNumber) => {
    setCurrentPageLastNews(pageNumber);
    // Removed scroll to top
  };

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </Spinner>
      </div>
    );
  }

  if (!isLoading && blogs.length === 0) {
    return (
      <div className="text-center my-5">
        <p>{t("noBlogsAvailable")}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          {t("retry")}
        </button>
      </div>
    );
  }

  return (
    <div className={`blog-promo-container ${isRTL ? "rtl" : ""}`}>
      <ToastContainer />
      <div className="promo-header">
        <span className="ccf-eyebrow">{t("lastNews")} &amp; {t("upcomingEvents")}</span>
        <h2 className="promo-title">{t("ourBlog")}</h2>
        <div className="heading-line"></div>
      </div>
      <div className="sections-container">
        {/* Upcoming News Section */}
        <section className="blog-section upcoming-news">
          <div className="section-header">
            <h2 className="section-titllle">{t("Upcoming News")}</h2>
          </div>
          <div className="blog-items">
            {currentUpcoming.length > 0 ? (
              currentUpcoming.map((blog) => (
                <Link
                  to={`/blog/${blog.id}`}
                  key={blog.id}
                  className="blog-item-link"
                >
                  <div className="blog-item">
                    <div className="date-block">
                      <span className="date-day">
                        {new Date(blog.date)
                          .getDate()
                          .toString()
                          .padStart(2, "0")}
                      </span>
                      <span className="date-month">
                        {new Date(blog.date).toLocaleString(i18n.language, {
                          month: "long",
                        })}
                      </span>
                    </div>
                    <div className="event-details">
                      <h3 className="event-title">
                        {titleOf(blog)}
                      </h3>
                    </div>
                    <div className="event-image">
                      {blog.imageUrl ? (
                        <img
                          src={blog.imageUrl}
                          alt={titleOf(blog)}
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            toast.error(t("imageLoadError"));
                            e.target.style.display = "none"; // Hide the image if it fails to load
                          }}
                        />
                      ) : (
                        <div className="no-image-placeholder">
                          <p>{t("noImageAvailable")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <p className="text-center">{t("noUpcomingNews")}</p>
            )}
          </div>

          {/* Pagination for Upcoming News */}
          {totalPagesUpcoming > 1 && (
            <Pagination className="justify-content-center mt-4 custom-pagination">
              <Pagination.First
                onClick={() => handlePageChangeUpcoming(1)}
                disabled={currentPageUpcoming === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChangeUpcoming(currentPageUpcoming - 1)}
                disabled={currentPageUpcoming === 1}
              />
              {Array.from({ length: totalPagesUpcoming }, (_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={currentPageUpcoming === index + 1}
                  onClick={() => handlePageChangeUpcoming(index + 1)}
                  aria-label={`Go to page ${index + 1}`}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => handlePageChangeUpcoming(currentPageUpcoming + 1)}
                disabled={currentPageUpcoming === totalPagesUpcoming}
              />
              <Pagination.Last
                onClick={() => handlePageChangeUpcoming(totalPagesUpcoming)}
                disabled={currentPageUpcoming === totalPagesUpcoming}
              />
            </Pagination>
          )}
        </section>

        {/* Last News Section */}
        <section className="blog-section last-news">
          <div className="section-header">
            <h2 className="section-titllle">{t("Last News")}</h2>
          </div>
          <div className="blog-items">
            {currentLastNews.length > 0 ? (
              currentLastNews.map((blog) => (
                <Link
                  to={`/blog/${blog.id}`}
                  key={blog.id}
                  className="blog-item-link"
                >
                  <div className="blog-item">
                    <div className="date-block">
                      <span className="date-day">
                        {new Date(blog.date)
                          .getDate()
                          .toString()
                          .padStart(2, "0")}
                      </span>
                      <span className="date-month">
                        {new Date(blog.date).toLocaleString(i18n.language, {
                          month: "long",
                        })}
                      </span>
                    </div>
                    <div className="event-details">
                      <h3 className="event-title">
                        {titleOf(blog)}
                      </h3>
                    </div>
                    <div className="event-image">
                      {blog.imageUrl ? (
                        <img
                          src={blog.imageUrl}
                          alt={titleOf(blog)}
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            toast.error(t("imageLoadError"));
                            e.target.style.display = "none"; 
                          }}
                        />
                      ) : (
                        <div className="no-image-placeholder">
                          <p>{t("noImageAvailable")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <p className="text-center">{t("noLastNews")}</p>
            )}
          </div>

          {/* Pagination for Last News */}
          {totalPagesLastNews > 1 && (
            <Pagination className="justify-content-center mt-4 custom-pagination">
              <Pagination.First
                onClick={() => handlePageChangeLastNews(1)}
                disabled={currentPageLastNews === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChangeLastNews(currentPageLastNews - 1)}
                disabled={currentPageLastNews === 1}
              />
              {Array.from({ length: totalPagesLastNews }, (_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={currentPageLastNews === index + 1}
                  onClick={() => handlePageChangeLastNews(index + 1)}
                  aria-label={`Go to page ${index + 1}`}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => handlePageChangeLastNews(currentPageLastNews + 1)}
                disabled={currentPageLastNews === totalPagesLastNews}
              />
              <Pagination.Last
                onClick={() => handlePageChangeLastNews(totalPagesLastNews)}
                disabled={currentPageLastNews === totalPagesLastNews}
              />
            </Pagination>
          )}
        </section>
      </div>
      <div className="promo-footer">
        <Link to="/blog" className="promo-all-link">
          {t("viewAll", "View all posts")} <i className="fas fa-arrow-right"></i>
        </Link>
      </div>
    </div>
  );
};

export default BlogPromo;
